import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Lock } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    address: "",
    city: "",
    country: "",
    zip: "",
    cardName: "",
    cardNumber: "",
    cardExp: "",
    cardCvv: "",
  });
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  const c = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    setBusy(true);
    try {
      const total = subtotal;
      const res = await api.post("/orders", {
        customer_email: form.email,
        customer_name: form.name,
        items,
        subtotal,
        shipping: 0,
        tax: 0,
        total,
        shipping_address: { address: form.address, city: form.city, country: form.country, zip: form.zip },
      });
      setOrder(res.data);
      clearCart();
      toast.success("Order placed — confirmation sent");
    } catch (e) {
      toast.error("Could not complete order");
    } finally {
      setBusy(false);
    }
  };

  if (order) {
    return (
      <div className="bg-poche-black min-h-[70vh] flex items-center justify-center text-white py-20" data-testid="order-confirmation">
        <div className="text-center max-w-lg px-6">
          <CheckCircle2 size={48} className="text-poche-gold mx-auto" strokeWidth={1.2} />
          <p className="text-[11px] tracking-luxury uppercase text-poche-gold mt-6">Order Confirmed</p>
          <h2 className="font-serif text-5xl mt-3">Welcome to the house.</h2>
          <p className="text-white/65 mt-4">Order #{order.order_id.toUpperCase()} · Tracking {order.tracking_number}</p>
          <p className="text-white/65 mt-2">A confirmation has been dispatched to {order.customer_email}.</p>
          <div className="flex gap-4 justify-center mt-10">
            <Link to="/" className="btn-outline-gold">Return Home</Link>
            <Link to="/account" className="btn-gold">View My Account</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="bg-poche-black text-white">
      <section className="container-x py-16">
        <p className="text-[11px] tracking-luxury uppercase text-poche-gold">Checkout</p>
        <h1 className="font-serif text-5xl md:text-6xl mt-3">Complete your acquisition.</h1>

        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12">
          <div className="lg:col-span-7 space-y-10">
            <Section title="Contact">
              <Grid2>
                <Field label="Full Name" testid="ck-name" value={form.name} onChange={c("name")} required />
                <Field label="Email" testid="ck-email" type="email" value={form.email} onChange={c("email")} required />
              </Grid2>
            </Section>
            <Section title="Shipping Address">
              <Field label="Address" testid="ck-address" value={form.address} onChange={c("address")} required />
              <Grid2>
                <Field label="City" testid="ck-city" value={form.city} onChange={c("city")} required />
                <Field label="Country" testid="ck-country" value={form.country} onChange={c("country")} required />
              </Grid2>
              <Field label="Postal Code" testid="ck-zip" value={form.zip} onChange={c("zip")} required />
            </Section>
            <Section title="Payment (Mock)">
              <p className="text-[10px] tracking-luxury uppercase text-poche-gold/80 mb-4 flex items-center gap-2"><Lock size={12} /> Secure · For demo only · No real charge</p>
              <Field label="Name on Card" testid="ck-card-name" value={form.cardName} onChange={c("cardName")} required />
              <Field label="Card Number" testid="ck-card-number" value={form.cardNumber} onChange={c("cardNumber")} placeholder="4242 4242 4242 4242" required />
              <Grid2>
                <Field label="MM / YY" testid="ck-card-exp" value={form.cardExp} onChange={c("cardExp")} required />
                <Field label="CVV" testid="ck-card-cvv" value={form.cardCvv} onChange={c("cardCvv")} required />
              </Grid2>
            </Section>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-white/10 p-8 sticky top-28">
              <h3 className="font-serif text-2xl">Your Bag</h3>
              <ul className="mt-6 space-y-4 max-h-[260px] overflow-auto pr-2">
                {items.map((i) => (
                  <li key={i.product_id} className="flex gap-3">
                    <div className="bg-poche-ivory w-14 h-14 flex-shrink-0"><img src={i.image} alt="" className="w-full h-full object-cover" /></div>
                    <div className="flex-1 text-sm">
                      <div className="text-[10px] tracking-luxury uppercase text-white/55">{i.name}</div>
                      <div>{i.subtitle} <span className="text-white/45">× {i.qty}</span></div>
                    </div>
                    <div className="font-serif text-poche-gold">${(i.price * i.qty).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
              <div className="border-t border-white/15 mt-6 pt-6 flex items-center justify-between">
                <span className="text-[11px] tracking-luxury uppercase text-white/55">Total</span>
                <span className="font-serif text-3xl text-poche-gold" data-testid="ck-total">${subtotal.toLocaleString()}</span>
              </div>
              <button data-testid="place-order" disabled={busy} className="btn-gold w-full justify-center mt-6 disabled:opacity-60">
                {busy ? "Placing Order…" : "Place Order"}
              </button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-white/10 pt-8">
      <h3 className="text-[11px] tracking-luxury uppercase text-poche-gold mb-6">{title}</h3>
      <div className="space-y-5">{children}</div>
    </div>
  );
}
function Grid2({ children }) {
  return <div className="grid grid-cols-2 gap-5">{children}</div>;
}
function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] tracking-luxury uppercase text-white/55">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} required={required} placeholder={placeholder} className="w-full bg-transparent border-b border-white/25 focus:border-poche-gold py-3 text-white focus:outline-none placeholder:text-white/25" />
    </div>
  );
}
